<template>
  <items-listing-layout class="calendar-page">
    <template #header-left>
      <h2 class="calendar-page__title">Calendar</h2>
    </template>
    <template #main-header>
      <calendar-filter-bar
        v-model:selected-status-type="selectedStatusType"
        v-model:selected-project-ids="selectedProjectIds"
        v-model:selected-mailbox-ids="selectedMailboxIds"
        v-model:selected-assignee-ids="selectedAssigneeIds"
        v-model:selected-status-ids="selectedStatusIds"
        v-model:selected-priority-ids="selectedPriorityIds"
        :project-options="projectOptions"
        :mailbox-options="mailboxOptions"
        :assignee-options="assigneeOptions"
        :active-status-options="activeStatusOptions"
        :inactive-status-options="inactiveStatusOptions"
        :priority-options="priorityOptions"
        @reset-filters="resetFilters"
      />
    </template>
    <template #main-content>
      <v-calendar
        ref="calendar"
        :masks="{
          weekdays: 'WWW',
        }"
        :first-day-of-week="1"
        class="custom-calendar"
        disable-page-swipe
        @did-move="(pages: { month: number, year: number }[]) => handlePageUpdate(pages[0])"
      >
        <template #header-title-wrapper>
          <div class="px-4 py-2 flex items-center justify-between">
            <span>
              {{ new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' }) }} {{ year }}
            </span>
            <button
              class="ml-4 px-3 py-1 rounded bg-gray-200 text-sm font-medium border border-gray-300"
              type="button"
              @click="goToToday"
            >
              Today
            </button>
          </div>
        </template>
        <template #day-content="{ day }">
          <div class="calendar-page__day-cell">
            <span :class="['day-label', { 'day-label--today': isToday(day) }]">{{ day.day }}</span>
            <div class="calendar-page__day-items">
              <p
                v-for="item in getItemsForDay(day)"
                :key="`${item.entityType}-${item.id}`"
                class="calendar-item"
                @click.stop="handleItemClick(item)"
              >
                <span
                  :class="{
                    'calendar-item__dot--task': item.entityType === 'task',
                    'calendar-item__dot--convo': item.entityType === 'conversation',
                    'calendar-item__dot--overdue': item.isOverdue,
                  }"
                  class="calendar-item__dot"
                >&#11044;</span>
                <span class="calendar-item__title">{{ item.title }}</span>
              </p>
            </div>
          </div>
        </template>
      </v-calendar>
    </template>
    <template #modals>
      <task-detail-drawer
        v-model:show-drawer="showTaskDrawer"
        v-model:task="selectedDrawerItem"
      />
      <conversation-detail-drawer
        v-model:show-drawer="showConvoDrawer"
        v-model:conversation="selectedDrawerItem"
      />
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import CalendarFilterBar from './components/calendar-filter-bar.vue'
import TaskDetailDrawer from '@/components/item/task-detail-drawer.vue'
import ConversationDetailDrawer from '@/components/item/conversation-detail-drawer.vue'
import TextAssistant from '@/helpers/text-assistant'
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'
import {
  ITasksOutputSearchTask,
  IConvosOutputSearchConvo,
  IProjectForSelection,
  IPersonWithEmail,
  IStatusForSelection,
  IPriorityLevelForSelection,
  IPersonBase,
  ILinkedItemForListing,
} from '@/api/types/response.types'

interface ICalendarItem {
  id: number
  title: string
  refNumber: number
  dueDate: string
  entityType: 'task' | 'conversation'
  parentId: number
  parentName: string
  status: IStatusForSelection
  priority: IPriorityLevelForSelection
  assignedTo: IPersonBase
  isOverdue: boolean
}

export default defineComponent({
  name: 'Calendar',

  components: {
    ItemsListingLayout,
    CalendarFilterBar,
    TaskDetailDrawer,
    ConversationDetailDrawer,
  },

  data() {
    const now = new Date()
    return {
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      isLoading: true,
      calendarItems: [] as ICalendarItem[],
      // Filter options
      projectOptions: [] as IProjectForSelection[],
      mailboxOptions: [] as IProjectForSelection[],
      assigneeOptions: [] as IPersonWithEmail[],
      allActiveStatusOptions: [] as IStatusForSelection[],
      allInactiveStatusOptions: [] as IStatusForSelection[],
      activeStatusOptions: [] as IStatusForSelection[],
      inactiveStatusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      // Selected filters
      selectedProjectIds: [] as number[],
      selectedMailboxIds: [] as number[],
      selectedAssigneeIds: [] as number[],
      selectedStatusIds: [] as number[],
      selectedStatusType: 'active',
      selectedPriorityIds: [] as number[],
      // Drawer state
      showTaskDrawer: false,
      showConvoDrawer: false,
      selectedDrawerItem: {} as ILinkedItemForListing,
    }
  },

  head: {
    title: 'Calendar',
  },

  computed: {
    accountID(): string {
      return this.$route.params.accountId
    },
    entityId(): number | null {
      return this.$route.query.entity_id
        ? Number(this.$route.query.entity_id)
        : null
    },
    dateRange(): { start: string; end: string } {
      const start = dayjs(new Date(this.year, this.month - 1, 1))
        .startOf('month')
        .format('YYYY-MM-DD')
      const end = dayjs(new Date(this.year, this.month - 1, 1))
        .endOf('month')
        .format('YYYY-MM-DD')
      return { start, end }
    },
    itemsByDate(): Record<string, ICalendarItem[]> {
      const map: Record<string, ICalendarItem[]> = {}
      for (const item of this.calendarItems) {
        if (!item.dueDate) {
          continue
        }
        const key = dayjs(item.dueDate).format('D')
        if (!map[key]) {
          map[key] = []
        }
        map[key].push(item)
      }
      return map
    },
  },

  watch: {
    selectedProjectIds: {
      deep: 1,
      handler() {
        this.refreshStatusOptions()
        this.fetchCalendarData()
      },
    },
    selectedMailboxIds: {
      deep: 1,
      handler() {
        this.refreshStatusOptions()
        this.fetchCalendarData()
      },
    },
    selectedAssigneeIds: {
      deep: 1,
      handler() {
        this.fetchCalendarData()
      },
    },
    selectedStatusIds: {
      deep: 1,
      handler() {
        this.fetchCalendarData()
      },
    },
    selectedStatusType() {
      this.fetchCalendarData()
    },
    selectedPriorityIds: {
      deep: 1,
      handler() {
        this.fetchCalendarData()
      },
    },
  },

  async created() {
    this.isLoading = true
    await this.fetchFilterOptions()

    if (this.entityId) {
      if (this.projectOptions.some(p => p.id === this.entityId)) {
        this.selectedProjectIds = [this.entityId]
      } else if (this.mailboxOptions.some(m => m.id === this.entityId)) {
        this.selectedMailboxIds = [this.entityId]
      }
    }

    await this.fetchCalendarData()
    this.isLoading = false
  },

  methods: {
    async fetchFilterOptions() {
      try {
        const [taskFilterOptions, convoFilterOptions] = await Promise.all([
          TasksAPI.get_All_FilterOptions(),
          ConversationsAPI.get_All_FilterOptions(),
        ])

        this.projectOptions = taskFilterOptions.listInternalProjects
        this.mailboxOptions = convoFilterOptions.listMailboxes
        this.priorityOptions = taskFilterOptions.listPriorities

        // Combine assignees from both, deduplicated by id
        const assigneeMap = new Map<number, IPersonWithEmail>()
        for (const a of taskFilterOptions.listAssignees) {
          assigneeMap.set(a.id, a)
        }
        for (const a of convoFilterOptions.listAssignees) {
          assigneeMap.set(a.id, a)
        }
        this.assigneeOptions = Array.from(assigneeMap.values())

        // Combine active statuses from both, deduplicated by id
        const activeStatusMap = new Map<number, IStatusForSelection>()
        for (const s of taskFilterOptions.listActiveStatuses) {
          activeStatusMap.set(s.id, s)
        }
        for (const s of convoFilterOptions.listActiveStatuses) {
          activeStatusMap.set(s.id, s)
        }
        this.allActiveStatusOptions = Array.from(activeStatusMap.values())
        this.activeStatusOptions = this.allActiveStatusOptions

        // Combine inactive statuses from both, deduplicated by id
        const inactiveStatusMap = new Map<number, IStatusForSelection>()
        for (const s of taskFilterOptions.listInactiveStatuses) {
          inactiveStatusMap.set(s.id, s)
        }
        for (const s of convoFilterOptions.listInactiveStatuses) {
          inactiveStatusMap.set(s.id, s)
        }
        this.allInactiveStatusOptions = Array.from(inactiveStatusMap.values())
        this.inactiveStatusOptions = this.allInactiveStatusOptions
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async refreshStatusOptions() {
      const hasProjectFilter = this.selectedProjectIds.length > 0
      const hasMailboxFilter = this.selectedMailboxIds.length > 0

      if (!hasProjectFilter && !hasMailboxFilter) {
        // No source filter — show all statuses
        this.activeStatusOptions = this.allActiveStatusOptions
        this.inactiveStatusOptions = this.allInactiveStatusOptions
        return
      }

      try {
        const promises: Promise<{ listStatuses: IStatusForSelection[] }>[] = []

        for (const pid of this.selectedProjectIds) {
          promises.push(TasksAPI.get_FilterOptions(pid))
        }
        for (const mid of this.selectedMailboxIds) {
          promises.push(ConversationsAPI.get_FilterOptions(mid))
        }

        const results = await Promise.all(promises)

        // Collect status IDs from the selected entities
        const validStatusIds = new Set<number>()
        for (const result of results) {
          for (const s of result.listStatuses) {
            validStatusIds.add(s.id)
          }
        }

        // Filter the baseline lists to only include valid statuses
        this.activeStatusOptions = this.allActiveStatusOptions.filter(s =>
          validStatusIds.has(s.id),
        )
        this.inactiveStatusOptions = this.allInactiveStatusOptions.filter(s =>
          validStatusIds.has(s.id),
        )

        // Remove any selected status IDs that are no longer valid
        const cleanedStatusIds = this.selectedStatusIds.filter(id =>
          validStatusIds.has(id),
        )
        if (cleanedStatusIds.length !== this.selectedStatusIds.length) {
          this.selectedStatusIds = cleanedStatusIds
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async fetchCalendarData() {
      try {
        const { start, end } = this.dateRange
        const assigneeIds = TextAssistant.convertNumberArrayToStringList(
          this.selectedAssigneeIds,
        )
        const statusIds = TextAssistant.convertNumberArrayToStringList(
          this.selectedStatusIds,
        )
        const priorityIds = TextAssistant.convertNumberArrayToStringList(
          this.selectedPriorityIds,
        )
        const projectIds = TextAssistant.convertNumberArrayToStringList(
          this.selectedProjectIds,
        )
        const mailboxIds = TextAssistant.convertNumberArrayToStringList(
          this.selectedMailboxIds,
        )

        const hasProjectFilter = this.selectedProjectIds.length > 0
        const hasMailboxFilter = this.selectedMailboxIds.length > 0

        // If projects are selected but no mailboxes, only fetch tasks.
        // If mailboxes are selected but no projects, only fetch conversations.
        // If both or neither are selected, fetch both.
        const shouldFetchTasks = !hasMailboxFilter || hasProjectFilter
        const shouldFetchConvos = !hasProjectFilter || hasMailboxFilter

        const taskPromise = shouldFetchTasks
          ? TasksAPI.get_Search(
              1, // page
              projectIds, // internal_project_ids
              '', // creator_ids
              '', // sort
              priorityIds, // priority_ids
              statusIds, // status_ids
              this.selectedStatusType, // status_type
              assigneeIds, // assignee_ids
              false, // assigned_not_to_you
              '', // job_appointee_groupings_string
              '', // including_tag_ids
              '', // not_including_tag_ids
              null, // due_type
              start, // start_due_date
              end, // end_due_date
              '', // search_term
              false, // only_watching
              null, // created_within_days
              null, // last_updated_within_days
            )
          : null

        const convoPromise = shouldFetchConvos
          ? ConversationsAPI.get_Search(
              1, // page
              mailboxIds, // mailbox_ids
              '', // customer_ids
              '', // sort
              false, // is_spam
              priorityIds, // priority_ids
              statusIds, // status_ids
              this.selectedStatusType, // status_type
              assigneeIds, // assignee_ids
              false, // assigned_not_to_you
              '', // job_appointee_groupings_string
              '', // including_tag_ids
              '', // not_including_tag_ids
              null, // due_type
              start, // start_due_date
              end, // end_due_date
              '', // search_term
              false, // only_watching
              null, // created_within_days
              null, // last_updated_within_days
            )
          : null

        const [taskResponse, convoResponse] = await Promise.all([
          taskPromise,
          convoPromise,
        ])

        const taskItems: ICalendarItem[] = taskResponse
          ? taskResponse.listTasks
              .filter((t: ITasksOutputSearchTask) => t.dueDate)
              .map((t: ITasksOutputSearchTask) => ({
                id: t.id,
                title: t.title,
                refNumber: t.refNumber,
                dueDate: t.dueDate,
                entityType: 'task' as const,
                parentId: t.project.id,
                parentName: t.project.name,
                status: t.status,
                priority: t.priority,
                assignedTo: t.assignedTo,
                isOverdue: t.isOverdue,
              }))
          : []

        const convoItems: ICalendarItem[] = convoResponse
          ? convoResponse.listConversations
              .filter((c: IConvosOutputSearchConvo) => c.dueDate)
              .map((c: IConvosOutputSearchConvo) => ({
                id: c.id,
                title: c.title,
                refNumber: c.refNumber,
                dueDate: c.dueDate,
                entityType: 'conversation' as const,
                parentId: c.mailbox.id,
                parentName: c.mailbox.name,
                status: c.status,
                priority: c.priority,
                assignedTo: c.assignedTo,
                isOverdue: c.isOverdue,
              }))
          : []

        this.calendarItems = [...taskItems, ...convoItems]
      } catch (error) {
        this.$error.emit(error)
      }
    },

    isToday(day: any): boolean {
      const now = new Date()
      return (
        day.day === now.getDate() &&
        day.month === now.getMonth() + 1 &&
        day.year === now.getFullYear()
      )
    },

    getItemsForDay(day: any): ICalendarItem[] {
      return this.itemsByDate[day.day] || []
    },

    handlePageUpdate(e: any) {
      if (e.month !== this.month || e.year !== this.year) {
        this.year = e.year
        this.month = e.month
        this.fetchCalendarData()
      }
    },

    goToToday() {
      const today = new Date()
      const calendar = this.$refs.calendar as any
      calendar.move({
        month: today.getMonth() + 1,
        year: today.getFullYear(),
      })
    },

    mapToLinkedItem(item: ICalendarItem): ILinkedItemForListing {
      return {
        id: item.id,
        refNumber: item.refNumber,
        title: item.title,
        dueDate: item.dueDate,
        entityAccessible: true,
        entity: { id: item.parentId, name: item.parentName },
        assignedTo: item.assignedTo,
        status: item.status,
        priority: item.priority,
        isActive: true,
      } as ILinkedItemForListing
    },

    handleItemClick(item: ICalendarItem) {
      this.selectedDrawerItem = this.mapToLinkedItem(item)
      if (item.entityType === 'task') {
        this.showTaskDrawer = true
      } else {
        this.showConvoDrawer = true
      }
    },

    resetFilters() {
      this.selectedProjectIds = []
      this.selectedMailboxIds = []
      this.selectedAssigneeIds = []
      this.selectedStatusIds = []
      this.selectedStatusType = 'active'
      this.selectedPriorityIds = []
    },
  },
})
</script>

<style lang="scss">
.calendar-page {
  &__title {
    margin: 0;
    font-weight: 600;
  }

  &__cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    border-bottom: 1px solid #dde3ea;
  }

  &__cal-month {
    font-weight: 600;
    color: var(--color-text-secondary);
  }

  &__today-btn {
    padding: 4px 12px;
    border-radius: 4px;
    background-color: var(--color-background-secondary, #f0f0f0);
    font-size: 13px;
    font-weight: 500;
    border: 1px solid var(--color-border-primary, #d0d0d0);
    cursor: pointer;
    color: var(--color-text-secondary);

    &:hover {
      background-color: var(--color-background-third, #e0e0e0);
    }
  }

  &__day-cell {
    display: flex;
    flex-direction: column;
    height: 100%;
    z-index: 10;
    overflow: hidden;
  }

  &__day-items {
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .vc-container {
    --day-border: 1px solid #dde3ea;
    --day-border-highlight: 1px solid #b8c2cc;
    --day-width: 90px;
    --day-height: 140px;
    --weekday-bg: var(--color-background-primary);

    @include media-breakpoint-down(sm) {
      --day-width: 55px;
      --day-height: 100px;
    }

    border-radius: 0;
    width: 100%;

    & .vc-header {
      /*
      v-calendar 3 makes its headers 30px tall with 18px text, too small for the title and Today button. The arrows
      are a second header laid over the page's, so both get the same height for the arrows to center on the title.
      */
      height: 51px;
      margin-top: 0;
      padding: 0 10px 0 0;
      font-size: inherit;

      & .vc-arrow {
        /* The arrows are buttons now, which the browser would otherwise fill gray */
        background-color: transparent;

        &:hover {
          background-color: var(--vc-header-arrow-hover-bg);
        }
      }
    }
    & .vc-pane .vc-header {
      border-bottom: var(--day-border);
    }
    & .vc-pane {
      background-color: var(--color-background-primary);
      color: var(--color-text-secondary);
      font-weight: 500;
    }
    & .vc-weeks {
      padding: 0;
    }
    & .vc-weekday {
      padding: 5px 0;
      font-weight: normal;
      color: var(--color-text-secondary);
      background-color: var(--color-background-primary);
    }
    & .vc-day {
      padding: 0 5px 3px 5px;
      text-align: left;
      height: var(--day-height);
      min-width: var(--day-width);
      background-color: var(--color-background-primary);
      &:not(.on-bottom) {
        border-bottom: var(--day-border);
      }
      &:not(.on-right) {
        border-right: var(--day-border);
      }
      & .day-label {
        color: var(--color-text-secondary);
        @include media-breakpoint-down(sm) {
          font-size: 12px !important;
        }

        &--today {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background-color: var(--color-primary, #4a90d9);
          color: #fff;
          font-weight: 600;
        }
      }
    }
    & .vc-day-dots {
      margin-bottom: 5px;
    }
  }

  .calendar-item {
    font-size: 12px !important;
    font-weight: 500 !important;
    padding: 2px 0 0 2px;
    color: var(--color-text-secondary);
    cursor: pointer;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;

    @include media-breakpoint-down(sm) {
      font-size: 10px !important;
    }

    &:hover {
      text-decoration: underline;
    }

    &__dot {
      font-size: 6px;
      vertical-align: middle;
      margin-right: 2px;

      &--task {
        color: var(--color-primary, #4a90d9);
      }
      &--convo {
        color: var(--color-secondary, #67c23a);
      }
      &--overdue {
        color: var(--color-danger, #f56c6c);
      }
    }

    &__title {
      vertical-align: middle;
    }
  }
}
</style>
